import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useCRM } from '../../context/CRMContext';

const MassEmailModal = ({ selectedContacts, onClose }) => {
  const { emailTemplates, sendMassEmail, currentUser } = useCRM();
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [campaignName, setCampaignName] = useState('');
  const [isSending, setIsSending] = useState(false);

  const previewRecipient = selectedContacts[0];

  const handleSend = async () => {
    setIsSending(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    sendMassEmail({
      name: campaignName || `Kampanje ${new Date().toLocaleDateString()}`,
      templateId: selectedTemplate.id,
      recipients: selectedContacts,
      sender: currentUser.name
    });
    setIsSending(false);
    onClose();
  };

  const replaceVariables = (text, contact) => {
    if (!text || !contact) return text;
    return text
      .replace(/\[FirstName\]/g, contact.name.split(' ')[0])
      .replace(/\[Receiver.FirstName\]/g, contact.name.split(' ')[0])
      .replace(/\[Receiver.Company\]/g, 'Deres Bedrift')
      .replace(/\[Sender.Name\]/g, currentUser.name)
      .replace(/\[Sender.Company\]/g, 'Sales Work CRM'); // Renamed here
  };

  return (
    <div className="space-y-6 max-h-[80vh] overflow-y-auto px-1">
      {/* Progress Bar */}
      <div className="flex items-center justify-between mb-8 px-4">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div className={`flex items-center ${step >= s ? 'text-primary-600' : 'text-slate-300'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${step >= s ? 'border-primary-600 bg-primary-50 font-bold shadow-glow' : 'border-slate-200 bg-white'}`}>
                {step > s ? <SafeIcon icon="✅" className="w-5 h-5" /> : s}
              </div>
            </div>
            {s < 3 && <div className={`flex-1 h-0.5 mx-4 transition-colors ${step > s ? 'bg-primary-600' : 'bg-slate-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-200">
              <h3 className="text-lg font-black text-slate-900 mb-2">Velg en e-postmal</h3>
              <p className="text-sm font-medium text-slate-500 mb-6">Velg malen du ønsker å sende til de {selectedContacts.length} valgte kontaktene.</p>
              <div className="grid grid-cols-1 gap-3">
                {emailTemplates.map(template => (
                  <button key={template.id} onClick={() => setSelectedTemplate(template)} className={`p-4 rounded-2xl border-2 text-left transition-all ${selectedTemplate?.id === template.id ? 'border-primary-600 bg-primary-50 shadow-md ring-1 ring-primary-600' : 'border-white bg-white hover:border-slate-200'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selectedTemplate?.id === template.id ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                        <SafeIcon icon="📄" className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-black text-slate-900">{template.name}</p>
                        <p className="text-xs font-bold text-slate-400 mt-0.5 line-clamp-1">{template.subject}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Gi kampanjen et navn (valgfritt)</label>
              <input type="text" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="F.eks. Oppfølging Webinar April" className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 text-sm font-bold focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all shadow-sm" />
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
            <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary-600/20 rounded-bl-full -mr-16 -mt-16" />
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center shadow-glow">
                  <SafeIcon icon="📧" className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-black tracking-tight">Forhåndsvisning</h3>
              </div>
              <div className="space-y-4 font-bold">
                <div className="flex border-b border-slate-800 pb-3">
                  <span className="text-slate-500 w-20 text-xs uppercase tracking-widest">Til:</span>
                  <span className="text-sm">{previewRecipient.email}</span>
                </div>
                <div className="flex border-b border-slate-800 pb-3">
                  <span className="text-slate-500 w-20 text-xs uppercase tracking-widest">Emne:</span>
                  <span className="text-sm">{replaceVariables(selectedTemplate.subject, previewRecipient)}</span>
                </div>
                <div className="pt-4 text-slate-300 text-sm leading-relaxed whitespace-pre-wrap font-medium h-48 overflow-y-auto custom-scrollbar">
                  {replaceVariables(selectedTemplate.body, previewRecipient)}
                </div>
              </div>
              <div className="mt-6 flex items-center gap-2 text-xs font-black text-slate-500 uppercase tracking-widest bg-slate-800/50 p-3 rounded-xl border border-slate-700">
                <SafeIcon icon="🎯" className="w-4 h-4 text-primary-400" /> Viser forhåndsvisning for 1 av {selectedContacts.length} mottakere
              </div>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="step3" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12 space-y-6">
            <div className="w-24 h-24 bg-primary-50 rounded-[2rem] flex items-center justify-center mx-auto text-primary-600 border-2 border-primary-100 shadow-xl shadow-primary-500/10">
              <SafeIcon icon="📤" className="w-10 h-10" />
            </div>
            <div className="space-y-2">
              <h3 className="text-3xl font-black text-slate-900 tracking-tight">Klar til å sende?</h3>
              <p className="text-slate-500 font-medium text-lg">
                Du sender nå <span className="text-primary-600 font-black">"{selectedTemplate.name}"</span> til <span className="text-slate-900 font-black">{selectedContacts.length} kontakter.</span>
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-100 p-4 rounded-2xl text-amber-800 text-sm font-bold flex items-center gap-3 max-w-sm mx-auto">
              <div className="w-8 h-8 bg-amber-200 rounded-lg flex items-center justify-center flex-shrink-0">!</div>
              <p className="text-left">Dette kan ikke angres når sendingen har startet.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between pt-8 border-t border-slate-100">
        <button onClick={() => step > 1 ? setStep(step - 1) : onClose()} disabled={isSending} className="flex items-center gap-2 px-6 py-3 text-slate-600 font-black text-xs uppercase tracking-widest hover:text-slate-900 transition-colors disabled:opacity-50">
          <SafeIcon icon="⬅️" className="w-4 h-4" /> {step === 1 ? 'Avbryt' : 'Tilbake'}
        </button>
        <button onClick={() => { if (step === 3) handleSend(); else setStep(step + 1); }} disabled={!selectedTemplate || isSending} className="flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-[1.25rem] font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 disabled:opacity-50 disabled:cursor-not-allowed group">
          {isSending ? (
            <>
              <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}>
                <SafeIcon icon="🔄" className="w-5 h-5" />
              </motion.div> Sender...
            </>
          ) : (
            <>
              {step === 3 ? 'Start Utsending' : 'Neste steg'} <SafeIcon icon="➡️" className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MassEmailModal;